import { Box, Grid, List, ListItemButton, ListItemIcon, ListItemText, Paper, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import AssessmentPeriodClient, { AssessmentPeriod, AssessmentPeriodErrors } from "../apiClients/AssessmentPeriodClient";
import { UpsertAction } from "../Enums";
import DeleteIcon from "@mui/icons-material/Delete";
import ConditionalDisplay from "../components/ConditionalDisplay";
import { FormatDate } from "../Helpers";
import IconButtonEx from "../components/IconButtonEx";
import AssessmentPeriodUpsertPopup from "../popups/AssessmentPeriodUpsertPopup";
import useViewportMode from "../hooks/useViewportMode";
import { I18N } from "../I18N";


const AssessmentPeriodListComponent = () => {

    const defaultAssessmentPeriod = {
        Id: '',
        OrganizationId: '',
        StartDate: '',
        EndDate: ''
    }

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>();
    const { mode: viewportMode, isDesktop, isTablet, isLandscape } = useViewportMode();

    const [assessmentPeriods, setAssessmentPeriods] = useState<AssessmentPeriod[]>([]);
    const [listItemSelected, setListItemSelected] = useState<AssessmentPeriod>(defaultAssessmentPeriod);

    const loadData = async () => {
        try {
            setBusy(true);

            let ap = await new AssessmentPeriodClient().GetAssessmentPeriods();
            setAssessmentPeriods(ap);

            if (ap.length > 0) {
                await setListItemSelected(ap[0]);
            }

        }
        catch (error) {
            setError(error, AssessmentPeriodErrors.GetAllAssessmentPeriod);
        }
        finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    const handleDelete = async (id: string) => {
        try {
            setBusy(true);

            await new AssessmentPeriodClient().DeleteAssessmentPeriod(id)
                .then(() => loadData());
        }
        catch (error) {
            setError(error, AssessmentPeriodErrors.GetAllAssessmentPeriod);
        }
        finally {
            setBusy(false);
        }
    }

    const handleListItemClick = (e, i: string) => {
        let ap = assessmentPeriods.find(f => f.Id === i);
        setListItemSelected(ap);
    };

    const handleOnClose = () => {
        loadData();
    }

    if (!initialized.current) {
        return <></>
    }
    return (
        <Box data-viewport-mode={viewportMode}>
            <ErrorDisplay />
            <BusySpinner busy={busy}>
                {isDesktop ? <Grid container>
                    {/* Assessment Period List */}
                    <Grid item xs={6}>
                        <Box sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper', marginLeft: '1em', marginTop: '.5em' }}>
                            <List>
                                <Stack direction='row' sx={{ padding: '.5em'}}>
                                <AssessmentPeriodUpsertPopup
                                    mode={UpsertAction.Create}
                                    onClose={handleOnClose}
                                    visibleLabel={I18N.Translate('Add Assessment Period')}
                                    aria-label={I18N.Translate('Add Assessment Period')} />
                                    {/* <DisplayDialogContent
                                        title='Create Assessment Period'
                                        mode={UpsertAction.Create}
                                        fullWidth={false}
                                        content={
                                            <AssessmentPeriodUpsertComponent
                                                mode={UpsertAction.Create} />
                                        }
                                        hoverContent=''
                                        busy={false}
                                        icon={GetUpsertIcon(UpsertAction.Create)}
                                        onClose={handleOnClose} /> */}
                                </Stack>

                                {assessmentPeriods.map(ap =>
                                    <ListItemButton key={ap.Id}
                                        selected={listItemSelected.Id === ap.Id}
                                        onClick={(event) => handleListItemClick(event, ap.Id)}>
                                            <ListItemText primary={`${FormatDate(ap.StartDate)} - ${FormatDate(ap.EndDate)}`} />
                                            <ConditionalDisplay ifTrue={listItemSelected.Id === ap.Id}>
                                                <ListItemIcon>
                                                    <IconButtonEx size='small' hoverContent='Remove Assessment Period' message='Are you sure you want to remove this Assessment Period?' onClick={() => handleDelete(ap?.Id)}><DeleteIcon htmlColor='red' fontSize='small' /></IconButtonEx>
                                                </ListItemIcon>
                                            </ConditionalDisplay>
                                    </ListItemButton>
                                )}
                            </List>
                        </Box>
                    </Grid>
                    {/* Assessment Period Content */}
                    <Grid item xs={6} >
                        {/* <Box sx={{ width: '100%', bgcolor: 'background.paper', paddingRight: '1em' }}>
                            <ConditionalDisplay ifTrue={assessmentPeriods.length > 0}>
                                <AssessmentPeriodUpsertComponent mode={UpsertAction.View} inAssessmentPeriod={listItemSelected} />
                            </ConditionalDisplay>
                        </Box> */}
                    </Grid>
                </Grid> : <Stack spacing={1.5}>
                    <Paper variant="outlined">
                        <Stack direction='row' alignItems='center' spacing={1} sx={{ minHeight: 52, px: 1.25 }}>
                            <AssessmentPeriodUpsertPopup
                                mode={UpsertAction.Create}
                                onClose={handleOnClose}
                                visibleLabel={I18N.Translate('Add Assessment Period')}
                                aria-label={I18N.Translate('Add Assessment Period')} />
                        </Stack>
                    </Paper>
                    <Grid container spacing={1.5}>
                        {assessmentPeriods.map(ap => (
                            <Grid item xs={12} sm={isTablet && isLandscape ? 6 : 12} key={ap.Id}>
                                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                    <ListItemButton
                                        selected={listItemSelected.Id === ap.Id}
                                        onClick={(event) => handleListItemClick(event, ap.Id)}
                                        sx={{ minHeight: 56, py: 1, pl: 2, pr: 0.5 }}>
                                        <ListItemText
                                            primary={`${FormatDate(ap.StartDate)} - ${FormatDate(ap.EndDate)}`}
                                            secondary={listItemSelected.Id === ap.Id ? 'Selected period' : undefined}
                                            primaryTypographyProps={{ fontWeight: 700 }}
                                        />
                                        <ConditionalDisplay ifTrue={listItemSelected.Id === ap.Id}>
                                            <ListItemIcon sx={{ minWidth: 44 }} onClick={event => event.stopPropagation()}>
                                                <IconButtonEx
                                                    size='small'
                                                    hoverContent='Remove Assessment Period'
                                                    message='Are you sure you want to remove this Assessment Period?'
                                                    onClick={() => handleDelete(ap.Id)}
                                                    sx={{ minWidth: 44, minHeight: 44 }}>
                                                    <DeleteIcon htmlColor='red' fontSize='small' />
                                                </IconButtonEx>
                                            </ListItemIcon>
                                        </ConditionalDisplay>
                                    </ListItemButton>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Stack>}
            </BusySpinner>
        </Box>
    )
};

export default AssessmentPeriodListComponent;
